import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellSimpleRinging, PaperPlaneTilt, CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import api from "@/lib/api";

// Per-institution admission alerts: visitors opt in with email (+ optional
// WhatsApp); managers/admins can broadcast an update to all subscribers.
export default function AdmissionAlerts({ instId, instName, canManage = false }) {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null);
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [busy, setBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) api.get(`/institutions/${instId}/alerts/count`).then((r) => setCount(r.data.count)).catch(() => {});
  }, [open, instId]);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return toast.error("Please enter a valid email");
    setBusy(true);
    try {
      await api.post(`/institutions/${instId}/alerts/subscribe`, { email: email.trim(), whatsapp: whatsapp.trim() });
      toast.success("You're subscribed — we'll email you admission updates.");
      setEmail(""); setWhatsapp(""); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't subscribe — try again");
    }
    setBusy(false);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Write a message to send");
    setBusy(true);
    try {
      const { data } = await api.post(`/institutions/${instId}/alerts/send`, { subject: subject.trim(), message: message.trim() });
      toast.success(`Sent to ${data.sent} subscriber${data.sent === 1 ? "" : "s"}.`);
      setSubject(""); setMessage(""); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary" size="sm" className="rounded-full gap-1.5" data-testid="admission-alerts-btn">
          <BellSimpleRinging size={15} weight="fill" /> Admission alerts
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="admission-alerts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <BellSimpleRinging size={18} weight="fill" className="text-secondary" /> Admission alerts
          </DialogTitle>
          <DialogDescription>
            {canManage
              ? `Notify everyone subscribed to ${instName} about admission dates or news.`
              : `Get notified about admission dates, results and updates for ${instName}.`}
          </DialogDescription>
        </DialogHeader>

        {canManage ? (
          <form onSubmit={send} className="space-y-3">
            <div className="rounded-lg bg-muted/40 px-3 py-2 text-xs text-muted-foreground" data-testid="alerts-sub-count">
              {count === null ? "…" : `${count} subscriber${count === 1 ? "" : "s"} will receive this.`}
            </div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (e.g. Admissions open for 2026)" data-testid="alert-subject" className="rounded-xl" />
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write the admission update…" rows={5} data-testid="alert-message" className="rounded-xl" />
            <Button type="submit" disabled={busy} className="w-full gap-1.5 rounded-full" data-testid="alert-send-btn">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="bold" />} Send to subscribers
            </Button>
          </form>
        ) : (
          <form onSubmit={subscribe} className="space-y-3">
            {count !== null && count > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="alerts-sub-count">{count} student{count === 1 ? "" : "s"} already following.</p>
            )}
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Email *</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="alert-email" className="rounded-xl" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">WhatsApp (optional)</label>
              <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="+91 98765 43210" data-testid="alert-whatsapp" className="rounded-xl" />
            </div>
            <Button type="submit" disabled={busy} className="w-full gap-1.5 rounded-full" data-testid="alert-subscribe-btn">
              {busy ? <CircleNotch size={16} className="animate-spin" /> : <BellSimpleRinging size={16} weight="fill" />} Notify me
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
